import { Injectable, inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface KeycloakTokenResponse {
  readonly access_token: string;
  readonly refresh_token: string;
  readonly token_type: string;
  readonly expires_in: number;
  readonly scope: string;
}

export interface KeycloakUserInfo {
  readonly sub: string;
  readonly email: string;
  readonly preferred_username?: string;
  readonly given_name?: string;
  readonly family_name?: string;
  readonly email_verified?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CustomKeycloakService {
  private readonly keycloak = inject(KeycloakService);
  private readonly userProfileSubject =
    new BehaviorSubject<KeycloakProfile | null>(null);

  public readonly userProfile$ = this.userProfileSubject.asObservable();

  /**
   * Initialize Keycloak with SSO support
   */
  async init(): Promise<boolean> {
    try {
      if (this.keycloak && this.keycloak.getKeycloakInstance()) {
        return this.keycloak.isLoggedIn();
      }

      console.log('Initializing Keycloak with SSO...');

      const authenticated = await this.keycloak.init({
        config: {
          url: environment.keycloak.url,
          realm: environment.keycloak.realm,
          clientId: environment.keycloak.clientId
        },
        initOptions: {
          onLoad: 'login-required',
          silentCheckSsoRedirectUri:
            `${window.location.origin  }/assets/silent-check-sso.html`,
          checkLoginIframe: true,
          enableLogging: true,
          pkceMethod: 'S256',
          flow: 'standard',
          responseMode: 'fragment',
          checkLoginIframeInterval: 5,
          messageReceiveTimeout: 10000
        },
        loadUserProfileAtStartUp: true
      });

      if (authenticated) {
        await this.loadUserProfile();

        this.setupTokenRefresh();
      } else {
        console.log('User not authenticated, will be redirected to login');
      }

      return authenticated;
    } catch (error) {
      console.warn(
        'Keycloak initialization failed, continuing without Keycloak:',
        error
      );
      return false;
    }
  }

  /**
   * Login with username and password
   */
  async loginWithCredentials(
    username: string,
    password: string
  ): Promise<KeycloakTokenResponse> {
    const tokenUrl = this.buildTokenUrl();
    const formData = this.buildTokenRequestData(username, password);

    try {
      const response = await this.makeTokenRequest(tokenUrl, formData);
      const tokenData = await this.parseTokenResponse(response);

      await this.storeTokens(tokenData);
      await this.loadUserProfileIfAvailable();

      return tokenData;
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error(
        `Authentication failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Load user profile
   */
  private async loadUserProfile(): Promise<void> {
    try {
      const profile = await this.keycloak.loadUserProfile();
      this.userProfileSubject.next(profile);
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  }

  /**
   * Get current access token with proper null safety
   */
  getAccessToken(): string | null {
    try {
      const keycloakInstance = this.keycloak?.getKeycloakInstance();
      return keycloakInstance?.token ?? this.getStoredAccessToken();
    } catch (error) {
      console.warn('Error getting Keycloak access token:', error);
      return this.getStoredAccessToken();
    }
  }

  /**
   * Get current refresh token with proper null safety
   */
  getRefreshToken(): string | null {
    try {
      const keycloakInstance = this.keycloak?.getKeycloakInstance();
      return keycloakInstance?.refreshToken ?? this.getStoredRefreshToken();
    } catch (error) {
      console.warn('Error getting Keycloak refresh token:', error);
      return this.getStoredRefreshToken();
    }
  }

  /**
   * Check if user is authenticated with proper null safety
   */
  isAuthenticated(): boolean {
    try {
      return this.keycloak?.isLoggedIn() ?? this.isStoredTokenValid();
    } catch (error) {
      console.warn('Error checking Keycloak authentication status:', error);
      return this.isStoredTokenValid();
    }
  }

  /**
   * Get user profile
   */
  getUserProfile(): KeycloakProfile | null {
    return this.userProfileSubject.value;
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    await this.keycloak.logout();
    this.userProfileSubject.next(null);
  }

  /**
   * Refresh token with proper null safety
   */
  async refreshToken(): Promise<boolean> {
    try {
      const keycloakInstance = this.keycloak?.getKeycloakInstance();
      return keycloakInstance ? await keycloakInstance.updateToken(30) : false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  /**
   * Get user roles
   */
  getUserRoles(): string[] {
    return this.keycloak.getUserRoles();
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    return this.keycloak.isUserInRole(role);
  }

  /**
   * Setup automatic token refresh for SSO
   */
  private setupTokenRefresh(): void {
    setInterval(async() => {
      try {
        if (this.keycloak) {
          const keycloakInstance = this.keycloak.getKeycloakInstance();
          if (keycloakInstance) {
            const isUpdated = await keycloakInstance.updateToken(30);
            if (isUpdated) {
              console.log('Token refreshed successfully');
            }
          }
        }
      } catch (error) {
        console.warn('Token refresh failed:', error);
        this.logout();
      }
    }, 30000);
  }

  /**
   * Login with SSO redirect
   */
  async loginWithSSO(): Promise<void> {
    try {
      await this.keycloak.login({
        redirectUri: window.location.origin,
        prompt: 'login'
      });
    } catch (error) {
      console.error('SSO login failed:', error);
      throw error;
    }
  }

  /**
   * Login with SSO redirect (silent)
   */
  async loginWithSSOSilent(): Promise<void> {
    try {
      await this.keycloak.login({
        redirectUri: window.location.origin,
        prompt: 'none'
      });
    } catch (error) {
      console.error('Silent SSO login failed:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated via SSO with proper null safety
   */
  isSSOAuthenticated(): boolean {
    const keycloakInstance = this.keycloak?.getKeycloakInstance();
    return this.isAuthenticated() && (keycloakInstance?.authenticated ?? false);
  }

  /**
   * Get SSO session info with proper null safety
   */
  getSSOSessionInfo(): Record<string, unknown> | null {
    try {
      const keycloakInstance = this.keycloak?.getKeycloakInstance();
      if (!keycloakInstance) return null;

      return {
        authenticated: keycloakInstance.authenticated,
        token: keycloakInstance.token,
        refreshToken: keycloakInstance.refreshToken,
        tokenParsed: keycloakInstance.tokenParsed,
        refreshTokenParsed: keycloakInstance.refreshTokenParsed,
        timeSkew: keycloakInstance.timeSkew,
        loginRequired: keycloakInstance.loginRequired
      };
    } catch (error) {
      console.warn('Error getting SSO session info:', error);
      return null;
    }
  }

  /**
   * Logout with SSO redirect
   */
  async logoutWithSSO(): Promise<void> {
    try {
      await this.keycloak.logout(window.location.origin);
      this.userProfileSubject.next(null);
    } catch (error) {
      console.error('SSO logout failed:', error);
      throw error;
    }
  }

  /**
   * Check if stored token is valid with proper null safety
   */
  isStoredTokenValid(): boolean {
    try {
      const token = localStorage.getItem('keycloak_access_token');
      const expires = localStorage.getItem('keycloak_token_expires');

      if (!token || !expires) {
        return false;
      }

      const expirationTime = Number.parseInt(expires, 10);
      const currentTime = Date.now();

      if (currentTime >= expirationTime - 30000) {
        this.clearStoredTokens();
        return false;
      }

      return true;
    } catch (error) {
      console.warn(
        'KeycloakService: Error checking stored token validity:',
        error
      );
      return false;
    }
  }

  /**
   * Get a stored access token
   */
  getStoredAccessToken(): string | null {
    return localStorage.getItem('keycloak_access_token');
  }

  /**
   * Get stored refresh token
   */
  getStoredRefreshToken(): string | null {
    return localStorage.getItem('keycloak_refresh_token');
  }

  /**
   * Clear stored tokens
   */
  clearStoredTokens(): void {
    localStorage.removeItem('keycloak_access_token');
    localStorage.removeItem('keycloak_refresh_token');
    localStorage.removeItem('keycloak_token_type');
    localStorage.removeItem('keycloak_expires_in');
    localStorage.removeItem('keycloak_scope');
    localStorage.removeItem('keycloak_token_expires');
  }

  /**
   * Get Keycloak URL
   */
  getKeycloakUrl(): string {
    return environment.keycloak.url;
  }

  /**
   * Get Keycloak realm
   */
  getKeycloakRealm(): string {
    return environment.keycloak.realm;
  }

  /**
   * Refresh stored token using refresh token with modern patterns
   */
  async refreshStoredToken(): Promise<boolean> {
    const refreshToken = this.getStoredRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const tokenUrl = this.buildTokenUrl();
      const formData = this.buildRefreshTokenRequestData(refreshToken);
      const response = await this.makeTokenRequest(tokenUrl, formData);
      const tokenData = await this.parseTokenResponse(response);

      this.storeTokensInLocalStorage(tokenData);
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      this.clearStoredTokens();
      return false;
    }
  }

  private buildRefreshTokenRequestData(refreshToken: string): URLSearchParams {
    const formData = new URLSearchParams();
    formData.append('grant_type', 'refresh_token');
    formData.append('client_id', environment.keycloak.clientId);
    formData.append('client_secret', environment.keycloak.clientSecret);
    formData.append('refresh_token', refreshToken);
    return formData;
  }

  private buildTokenUrl(): string {
    return `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/token`;
  }

  private buildTokenRequestData(
    username: string,
    password: string
  ): URLSearchParams {
    const formData = new URLSearchParams();
    formData.append('grant_type', 'password');
    formData.append('client_id', environment.keycloak.clientId);
    formData.append('client_secret', environment.keycloak.clientSecret);
    formData.append('username', username);
    formData.append('password', password);
    formData.append('scope', 'openid email profile');
    return formData;
  }

  private async makeTokenRequest(
    url: string,
    formData: URLSearchParams
  ): Promise<Response> {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response;
  }

  private async parseTokenResponse(
    response: Response
  ): Promise<KeycloakTokenResponse> {
    return response.json() as Promise<KeycloakTokenResponse>;
  }

  private async storeTokens(tokenData: KeycloakTokenResponse): Promise<void> {
    this.storeTokensInKeycloakInstance(tokenData);

    this.storeTokensInLocalStorage(tokenData);

    this.clearLegacyTokens();
  }

  private storeTokensInKeycloakInstance(
    tokenData: KeycloakTokenResponse
  ): void {
    const keycloakInstance = this.keycloak?.getKeycloakInstance();
    if (keycloakInstance) {
      keycloakInstance.token = tokenData.access_token;
      keycloakInstance.refreshToken = tokenData.refresh_token;
    } else {
      console.warn(
        'Keycloak instance not available, storing tokens in localStorage only'
      );
    }
  }

  private storeTokensInLocalStorage(tokenData: KeycloakTokenResponse): void {
    const expirationTime = Date.now() + tokenData.expires_in * 1000;

    localStorage.setItem('keycloak_access_token', tokenData.access_token);
    localStorage.setItem(
      'keycloak_refresh_token',
      tokenData.refresh_token || ''
    );
    localStorage.setItem('keycloak_token_type', tokenData.token_type);
    localStorage.setItem(
      'keycloak_expires_in',
      tokenData.expires_in.toString()
    );
    localStorage.setItem('keycloak_scope', tokenData.scope);
    localStorage.setItem('keycloak_token_expires', expirationTime.toString());
  }

  private clearLegacyTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  private async loadUserProfileIfAvailable(): Promise<void> {
    const keycloakInstance = this.keycloak?.getKeycloakInstance();
    if (keycloakInstance) {
      await this.loadUserProfile();
    }
  }
}
